import React, { useState, useMemo, useEffect } from 'react';
import {
  VStack,
  useBoolean,
  AspectRatio,
  ButtonGroup,
  Button,
  Box,
  StackDivider,
  Text,
  chakra,
} from '@chakra-ui/react';
import { ImageDropzone } from '@olo-web/components/molecules/ImageDropzone';
import { storage } from '@olo-web/utils/google/firebase';
import { useMerchantTheme } from '@domain/merchants/queries/useMerchantTheme';
import { useMerchantImages } from '@domain/merchants/hooks/useMerchantImages';
import { useUpdateMerchantTheme } from '@domain/merchants/mutations/useUpdateMerchantTheme';
import { ECustomThemeViews } from '@olo-web/types/enums';
import { useCustomThemeDraftDispatch, useCustomThemeDraftState } from '@olo-web/client-state';
import { PreviousImages } from './PreviousImages';
import { resizeFile } from '@olo-web/utils/common/functions';
import { Banner } from '@olo-web/components/atoms/Banner';
import { useToast } from '@olo-web/utils/common/hooks';

export const ThemeImage = () => {
  const { data: themeData } = useMerchantTheme();
  const { backgroundUrl, logoUrl, faviconUrl } = useMerchantImages();
  const [loading, setLoading] = useState(false);
  const { notify } = useToast();
  const themeDispatch = useCustomThemeDraftDispatch();
  const {
    backgroundUrl: bgUrlFromState,
    logoUrl: logoUrlFromState,
    faviconUrl: faviconUrlFromState,
    view,
  } = useCustomThemeDraftState();

  const isLogo = view === ECustomThemeViews.LOGO;
  const isFavicon = view === ECustomThemeViews.FAVICON;

  const { mutate, isLoading } = useUpdateMerchantTheme({
    onSuccess: () => {
      notify({
        status: 'success',
        title: `${isLogo ? 'Logo' : 'Background image'} updated`,
        description: 'Your changes are now live',
        variant: 'left-accent',
        position: 'bottom-right',
      });
    },
  });

  const [isEdit, setIsEdit] = useBoolean();

  useEffect(() => {
    const editOn = isFavicon ? !faviconUrl : isLogo ? !logoUrl : !backgroundUrl;
    if (editOn) {
      setIsEdit.on();
      return;
    }
    setIsEdit.off();
  }, [isLogo, logoUrl, backgroundUrl, isFavicon, faviconUrl, setIsEdit]);

  const actionType = useMemo(() => {
    if (isFavicon) return 'UPDATE_FAVICON_URL';
    if (isLogo) return 'UPDATE_LOGO_URL';
    return 'UPDATE_BACKGROUND_URL';
  }, [isFavicon, isLogo]);

  const dataUrlToFile = async (dataUrl: any, fileName: string): Promise<File> => {
    const res: Response = await fetch(dataUrl);
    const blob: Blob = await res.blob();
    return new File([blob], fileName, { type: 'image/png' });
  };

  const handleDrop = async (file): Promise<void> => {
    try {
      const maxSize = isFavicon ? 32 : isLogo ? 250 : 450;
      const dataUrl = await resizeFile(file, maxSize);
      const resizedFile = await dataUrlToFile(dataUrl, file?.name);
      setLoading(true);
      const ref = storage().ref().child(`/${themeData?.id}/${view}/${Date.now()}`);
      await ref.put(resizedFile);
      const url = await ref.getDownloadURL();
      themeDispatch({
        type: actionType,
        payload: url,
      });
      setIsEdit.off();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = (): void => {
    const theme = isFavicon
      ? { faviconUrl: faviconUrlFromState }
      : isLogo
      ? { logoUrl: logoUrlFromState }
      : { backgroundUrl: bgUrlFromState };
    mutate({ theme, docId: themeData!.id! });
  };

  const handleRestore = (): void => {
    themeDispatch({
      type: actionType,
      payload: '',
    });
  };

  const showSave = useMemo(() => {
    if (isEdit) return false;
    if (
      isFavicon &&
      faviconUrlFromState &&
      themeData &&
      faviconUrlFromState !== themeData.faviconUrl
    )
      return true;
    if (isLogo && logoUrlFromState && themeData && logoUrlFromState !== themeData.logoUrl)
      return true;
    if (!isLogo && bgUrlFromState && themeData && bgUrlFromState !== themeData.backgroundUrl)
      return true;
    return false;
  }, [isLogo, logoUrlFromState, themeData, bgUrlFromState, isEdit, isFavicon, faviconUrlFromState]);

  const showButtons = useMemo(() => {
    if (view === ECustomThemeViews.LOGO) return !!logoUrl;
    if (view === ECustomThemeViews.BACKGROUND_IMAGE) return !!backgroundUrl;
    if (view === ECustomThemeViews.FAVICON) return !!faviconUrl;
    return true;
  }, [view, logoUrl, backgroundUrl, faviconUrl]);

  return (
    <VStack w="100%" spacing={10} divider={<StackDivider />}>
      {isFavicon && (
        <Banner
          status="info"
          description={
            <Text>
              A favicon is the small image next to the page name in the browser tab. Favicons must
              be square and the recommended size is{' '}
              <chakra.span fontWeight="semibold">32 x 32 pixels</chakra.span> for web browsers. If
              you do not provide a custom favicon, your logo will be used.
            </Text>
          }
        />
      )}
      <VStack w="100%" spacing={6}>
        {showButtons && (
          <ButtonGroup justifyContent="flex-end" w="100%" size="sm">
            {showSave && (
              <>
                <Button variant="tertiary" colorScheme="danger" onClick={handleRestore}>
                  Restore
                </Button>
                <Button
                  variant="solid"
                  colorScheme="primary"
                  isLoading={isLoading}
                  onClick={handleSave}
                >
                  Save
                </Button>
              </>
            )}
            <Button variant="ghost" onClick={setIsEdit.toggle}>
              {isEdit ? 'Cancel' : 'Edit'}
            </Button>
          </ButtonGroup>
        )}
        {isEdit ? (
          <ImageDropzone onDrop={handleDrop} isLoading={loading} />
        ) : (
          <AspectRatio
            w="100%"
            ratio={4 / 3}
            objectFit={isLogo ? 'contain' : 'cover'}
            objectPosition="center"
          >
            <Box
              bg={`url(${isFavicon ? faviconUrl : isLogo ? logoUrl : backgroundUrl})`}
              borderRadius="lg"
              bgPos="center"
              bgSize={isLogo ? 'contain' : 'cover'}
              bgRepeat="no-repeat"
            />
          </AspectRatio>
        )}
      </VStack>
      <PreviousImages />
    </VStack>
  );
};
